import React, { useState, useEffect } from 'react';
import { FileText, Calendar, Clock, Download, CheckCircle, XCircle, AlertCircle, RefreshCw, Plus, Edit3, Paperclip, Trash2 } from 'lucide-react';
import api from '../services/api';
import NewRequestForm from '../components/NewRequestForm';
import { STANDARD_DOCUMENT_TYPES, fetchDocumentTypes, documentTypeOptions, documentTypeName } from '../utils/documentTypes';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './MyRequests.css';

const MyRequests = () => {
  const [attestationRequests, setAttestationRequests] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [customRequests, setCustomRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [activeTab, setActiveTab] = useState('attestations');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useMarkAsReadOnMount(['attestation', 'leave', 'custom_request']);
  const [submitting, setSubmitting] = useState(false);
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);

  // Edit Attestation state
  const [editingAttestation, setEditingAttestation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    attestationType: 'work_certificate',
    language: 'fr',
    purpose: '',
    additionalNotes: '',
    startDate: '',
    endDate: '',
  });

  // The document types are managed by the admin (names, removed ones)
  const [documentTypes, setDocumentTypes] = useState(STANDARD_DOCUMENT_TYPES);
  const attestationTypes = documentTypeOptions(documentTypes);

  useEffect(() => {
    fetchAllRequests();
    fetchDocumentTypes().then(setDocumentTypes);
  }, []);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      const [attestations, leaves, customs, balance] = await Promise.all([
        api.get('/attestations/my-requests'),
        api.get('/absences/me').catch(() => []),
        api.get('/requests/my-requests').catch(() => []),
        api.get('/absences/leave-balance/me').catch(() => null)
      ]);
      setAttestationRequests(attestations || []);
      setLeaveRequests(leaves || []);
      setCustomRequests(customs || []);
      setLeaveBalance(balance);
    } catch (error) {
      console.error('Error fetching requests:', error);
      setMessage({ type: 'error', text: 'Error loading requests.' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAttestationClick = (request) => {
    setEditingAttestation(request);
    setEditFormData({
      attestationType: request.attestationType || 'work_certificate',
      language: request.language || 'fr',
      purpose: request.purpose || '',
      additionalNotes: request.additionalNotes || '',
      startDate: request.startDate ? request.startDate.substring(0, 10) : '',
      endDate: request.endDate ? request.endDate.substring(0, 10) : '',
    });
  };

  const handleSaveEditAttestation = async (e) => {
    e.preventDefault();
    if (!editingAttestation) return;
    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      if (
        editFormData.startDate
        && editFormData.endDate
        && new Date(editFormData.endDate) < new Date(editFormData.startDate)
      ) {
        setMessage({ type: 'error', text: 'End date cannot be before start date.' });
        setSubmitting(false);
        return;
      }

      // Empty optional fields (dates) are rejected by the API validation: leave them out
      const payload = Object.fromEntries(Object.entries(editFormData).filter(([, value]) => value !== ''));
      await api.patch(`/attestations/${editingAttestation.id}`, payload);
      setMessage({ type: 'success', text: 'Attestation request updated successfully!' });
      setEditingAttestation(null);
      fetchAllRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error updating request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAttestation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pending attestation request?')) return;
    setSubmitting(true);
    try {
      await api.delete(`/attestations/${id}`);
      setMessage({ type: 'success', text: 'Request deleted successfully.' });
      fetchAllRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Error deleting request.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (requestId, fileName) => {
    try {
      await api.downloadAttestation(requestId, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessage({ type: 'error', text: `Error downloading certificate: ${error.message}` });
    }
  };

  const handleCustomFileDownload = async (requestId, fileType, fallbackName) => {
    try {
      const blob = await api.downloadBlob(`/requests/${requestId}/download/${fileType}`);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fallbackName || `${fileType}-file`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessage({ type: 'error', text: 'Error downloading file.' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
      case 'generated':
        return <CheckCircle size={18} className="status-icon success" />;
      case 'rejected':
        return <XCircle size={18} className="status-icon error" />;
      case 'pending':
        return <Clock size={18} className="status-icon pending" />;
      case 'revoked':
        return <RefreshCw size={18} className="status-icon revoked" />;
      default:
        return <AlertCircle size={18} className="status-icon" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      case 'pending':
        return 'status-pending';
      case 'generated':
        return 'status-generated';
      case 'revoked':
        return 'status-revoked';
      default:
        return '';
    }
  };

  const renderAttestationRequests = () => {
    if (attestationRequests.length === 0) {
      return (
        <div className="empty-state">
          <FileText size={64} />
          <h3>No Attestation Requests</h3>
          <p>You haven't submitted any attestation requests yet.</p>
        </div>
      );
    }

    return (
      <div className="requests-grid">
        {attestationRequests.map((request) => (
          <div key={request.id} className={`request-card ${getStatusClass(request.status)}`}>
            <div className="request-header">
              <div className="request-type">
                <FileText size={20} />
                <span>{documentTypeName(documentTypes, request.attestationType, request.language || 'fr')}</span>
              </div>
              <div className="request-status">
                {getStatusIcon(request.status)}
                <span className="status-text">{request.status}</span>
              </div>
            </div>

            <div className="request-body">
              {request.purpose && (
                <div className="request-field">
                  <label>Purpose:</label>
                  <p>{request.purpose}</p>
                </div>
              )}

              <div className="request-meta">
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>Created: {formatDate(request.createdAt)}</span>
                </div>
                {request.approvedDate && (
                  <div className="meta-item">
                    <CheckCircle size={14} />
                    <span>Approved: {formatDate(request.approvedDate)}</span>
                  </div>
                )}
              </div>

              {request.rejectionReason && (
                <div className="rejection-reason">
                  <strong>Rejection Reason:</strong>
                  <p>{request.rejectionReason}</p>
                </div>
              )}
            </div>

            <div className="request-actions">
              {request.status === 'pending' && (
                <>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEditAttestationClick(request)}
                    disabled={submitting}
                    style={{ marginRight: '6px' }}
                  >
                    <Edit3 size={14} /> Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteAttestation(request.id)}
                    disabled={submitting}
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              )}

              {request.status === 'generated' && request.generatedFilePath && (
                <button
                  className="btn btn-success"
                  onClick={() => handleDownload(request.id, `${request.attestationType}_${request.id}`)}
                >
                  <Download size={16} />
                  Download PDF
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderLeaveRequests = () => {
    return (
      <>
        {leaveBalance && (
          <div className="leave-balance-card">
            <h3>Solde Congés {leaveBalance.year}</h3>
            <div className="leave-balance-stats">
              <div className="balance-stat">
                <span className="stat-label">Droit</span>
                <span className="stat-value">{leaveBalance.earnedDays}</span>
              </div>
              <div className="balance-stat">
                <span className="stat-label">Pris</span>
                <span className="stat-value">{leaveBalance.usedDays}</span>
              </div>
              <div className="balance-stat highlight">
                <span className="stat-label">Solde</span>
                <span className="stat-value">{leaveBalance.availableDays}</span>
              </div>
            </div>
          </div>
        )}
        
        {leaveRequests.length === 0 ? (
          <div className="empty-state">
            <Calendar size={64} />
            <h3>No Leave Requests</h3>
            <p>You haven't submitted any leave requests yet.</p>
          </div>
        ) : (
          <div className="requests-grid">
        {leaveRequests.map((request) => (
          <div key={request.id} className={`request-card ${getStatusClass(request.status)}`}>
            <div className="request-header">
              <div className="request-type">
                <Calendar size={20} />
                <span>{request.leaveType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
              </div>
              <div className="request-status">
                {getStatusIcon(request.status)}
                <span className="status-text">{request.status}</span>
              </div>
            </div>

            <div className="request-body">
              <div className="request-field">
                <label>Duration:</label>
                <p>{formatDate(request.startDate)} - {formatDate(request.endDate)}</p>
              </div>

              {request.reason && (
                <div className="request-field">
                  <label>Reason:</label>
                  <p>{request.reason}</p>
                </div>
              )}

              <div className="request-meta">
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>Created: {formatDate(request.createdAt)}</span>
                </div>
              </div>

              {request.rejectionReason && (
                <div className="rejection-reason">
                  <strong>Rejection Reason:</strong>
                  <p>{request.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        ))}
          </div>
        )}
      </>
    );
  };

  const renderCustomRequests = () => {
    if (customRequests.length === 0) {
      return (
        <div className="empty-state">
          <Edit3 size={64} />
          <h3>No Custom Requests</h3>
          <p>You haven't submitted any custom requests yet.</p>
        </div>
      );
    }

    return (
      <div className="requests-grid">
        {customRequests.map((request) => (
          <div key={request.id} className={`request-card ${getStatusClass(request.status)}`}>
            <div className="request-header">
              <div className="request-type">
                <Edit3 size={20} />
                <span>{request.title}</span>
              </div>
              <div className="request-status">
                {getStatusIcon(request.status)}
                <span className="status-text">{request.status}</span>
              </div>
            </div>

            <div className="request-body">
              <div className="request-field">
                <label>Category:</label>
                <p><span className="category-badge">{request.category}</span></p>
              </div>

              {request.description && (
                <div className="request-field">
                  <label>Description:</label>
                  <p>{request.description}</p>
                </div>
              )}

              <div className="request-meta">
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>Created: {formatDate(request.createdAt)}</span>
                </div>
                {request.approvedDate && (
                  <div className="meta-item">
                    <CheckCircle size={14} />
                    <span>Approved: {formatDate(request.approvedDate)}</span>
                  </div>
                )}
              </div>

              {request.rejectionReason && (
                <div className="rejection-reason">
                  <strong>Rejection Reason:</strong>
                  <p>{request.rejectionReason}</p>
                </div>
              )}

              {request.responseNotes && (
                <div className="response-notes">
                  <strong>Response:</strong>
                  <p>{request.responseNotes}</p>
                </div>
              )}

              {request.attachedFilePath && (
                <div className="attachment-info">
                  <Paperclip size={14} />
                  <span>Attached file</span>
                </div>
              )}
            </div>

            <div className="request-actions">
              {request.attachedFilePath && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleCustomFileDownload(request.id, 'attached', `attachment-${request.id}`)}
                >
                  <Download size={16} />
                  Download Attachment
                </button>
              )}
              {request.responseFilePath && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleCustomFileDownload(request.id, 'response', `response-${request.id}`)}
                >
                  <Download size={16} />
                  Download Response File
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="my-requests-page">
      <div className="page-header">
        <div className="header-title">
          <h1>My Requests</h1>
          <p>Track all your attestation, leave, and custom requests in one place</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => setShowNewRequestForm(true)}>
            <Plus size={18} />
            New Request
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchAllRequests}>
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {showNewRequestForm && (
        <NewRequestForm
          onClose={() => setShowNewRequestForm(false)}
          onSuccess={() => {
            setMessage({ type: 'success', text: 'Request submitted successfully!' });
            fetchAllRequests();
          }}
        />
      )}

      {/* Edit Attestation Modal */}
      {editingAttestation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit Attestation Request</h2>
              <button className="close-btn" onClick={() => setEditingAttestation(null)}>×</button>
            </div>
            <form onSubmit={handleSaveEditAttestation} className="attestation-form">
              <div className="form-group">
                <label>Certificate Type *</label>
                <select
                  value={editFormData.attestationType}
                  onChange={(e) => setEditFormData({ ...editFormData, attestationType: e.target.value })}
                  required
                >
                  {attestationTypes.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Document Language *</label>
                <select
                  value={editFormData.language || 'fr'}
                  onChange={(e) => setEditFormData({ ...editFormData, language: e.target.value })}
                >
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="form-group">
                <label>Purpose</label>
                <textarea
                  value={editFormData.purpose}
                  onChange={(e) => setEditFormData({ ...editFormData, purpose: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={editFormData.startDate}
                    onChange={(e) => setEditFormData({ ...editFormData, startDate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={editFormData.endDate}
                    onChange={(e) => setEditFormData({ ...editFormData, endDate: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Additional Notes</label>
                <textarea
                  value={editFormData.additionalNotes}
                  onChange={(e) => setEditFormData({ ...editFormData, additionalNotes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingAttestation(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Update Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'attestations' ? 'active' : ''}`}
            onClick={() => setActiveTab('attestations')}
          >
            <FileText size={18} />
            Attestations
            <span className="tab-count">{attestationRequests.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'leaves' ? 'active' : ''}`}
            onClick={() => setActiveTab('leaves')}
          >
            <Calendar size={18} />
            Leave Requests
            <span className="tab-count">{leaveRequests.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'customs' ? 'active' : ''}`}
            onClick={() => setActiveTab('customs')}
          >
            <Edit3 size={18} />
            Custom Requests
            <span className="tab-count">{customRequests.length}</span>
          </button>
        </div>
      </div>

      <div className="requests-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading your requests...</p>
          </div>
        ) : (
          <>
            {activeTab === 'attestations' && renderAttestationRequests()}
            {activeTab === 'leaves' && renderLeaveRequests()}
            {activeTab === 'customs' && renderCustomRequests()}
          </>
        )}
      </div>
    </div>
  );
};

export default MyRequests;
